import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useRecruiterEnabled } from '@/hooks/useRecruiterEnabled';
import { useLiveTvEnabled } from '@/hooks/useLiveTvEnabled';

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
  isAuthenticated: boolean;
  displayName: string | null;
  admin: boolean;
  /**
   * `true` when the user has the access claim (either a real account or a
   * guest with a redeemed code). Gates the Sports + Sports VOD links so
   * unredeemed visitors don't see browse entries to features they can't use.
   * Mirrors the desktop nav check in `AppNav.tsx`.
   */
  access: boolean;
  canUseLobby: boolean;
  inLobby: boolean;
  lobbyBusy: boolean;
  lobbyCode: string | null;
  /** When true, render the "Join Lobby" CTA inside the in-lobby drawer block. */
  showJoinLobbyButton?: boolean;
  /** Click handler for the "Join Lobby" CTA. */
  onJoinLobby?: () => void;
  onCreate: () => void | Promise<void>;
  onLeave: () => void | Promise<void>;
  onJoin: (code: string) => void | Promise<void>;
  onSignOut: () => void;
}

/**
 * Right-side slide-in drawer used by AppNav below the --bp-lg
 * (960px) breakpoint. Purely presentational: all auth/lobby state
 * + handlers come from AppNav so the existing redux logic is
 * untouched.
 */
export function NavDrawer(props: NavDrawerProps): JSX.Element {
  const {
    open, onClose, isAuthenticated, displayName, admin, access,
    canUseLobby, inLobby, lobbyBusy, lobbyCode,
    showJoinLobbyButton, onJoinLobby,
    onCreate, onLeave, onJoin, onSignOut,
  } = props;

  const { t } = useTranslation();
  // Mirror the desktop nav's recruitment gate (isRecruiter && platform enabled).
  // autofetch:false for the same reason as AppNav — read-only snapshot access.
  const { isRecruiter } = useEntitlements({ autofetch: false });
  const recruiterEnabled = useRecruiterEnabled(access || admin);
  // Mirror the desktop nav's Live TV dark-ship gate ((access || admin) &&
  // backend-enabled). Shares the same module-level probe cache as AppNav, so
  // this adds no extra request.
  const liveTvEnabled = useLiveTvEnabled(access || admin);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const drawerRef = useRef<HTMLElement>(null);

  // Toggle the body scroll-lock class while open.
  useEffect(() => {
    if (open) document.documentElement.classList.add('is-drawer-open');
    else document.documentElement.classList.remove('is-drawer-open');
    return () => document.documentElement.classList.remove('is-drawer-open');
  }, [open]);

  // Escape-to-close and focus the first item on open.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    // Defer focus to after render — vitest/jsdom needs the tick.
    const t = window.setTimeout(() => {
      const first = drawerRef.current?.querySelector<HTMLElement>(
        'button, [href], input',
      );
      first?.focus();
    }, 0);
    return () => {
      document.removeEventListener('keydown', handler);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  // Reset the inline join form whenever the drawer closes.
  useEffect(() => {
    if (!open) {
      setShowJoinForm(false);
      setJoinCode('');
    }
  }, [open]);

  function handleJoin(e: React.FormEvent): void {
    e.preventDefault();
    const code = joinCode.trim();
    if (!code) return;
    void Promise.resolve(onJoin(code)).then(() => {
      setShowJoinForm(false);
      setJoinCode('');
    });
  }

  // Only render drawer contents when open. The drawer is a duplicate
  // of the desktop nav contents — keeping it mounted when closed would
  // make every existing `getByText` / `getByRole` query in AppNav tests
  // ambiguous (and would leak focus targets / accessible names into the
  // DOM at desktop widths where the drawer is purely decorative). The
  // backdrop/aside transition wrappers are still rendered every render
  // so the CSS slide animation still works on toggle.
  //
  // Portal to document.body so the drawer escapes the sticky `<nav>`'s
  // stacking context. Without the portal, the drawer is trapped at the
  // nav's z-index (20) and the brand text bleeds through on the left.
  return createPortal(
    <>
      <div
        className={`nav-drawer-backdrop${open ? ' is-open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
        data-testid="nav-drawer-backdrop"
      />
      <aside
        ref={drawerRef}
        id="nav-drawer"
        className={`nav-drawer${open ? ' is-open' : ''}`}
        role="dialog"
        aria-label={t('nav.menu')}
        aria-hidden={!open}
      >
        {open && (<>
        <div className="nav-drawer__header">
          <span className="nav-drawer__user">
            {isAuthenticated && displayName ? displayName : t('nav.menu')}
          </span>
          <button
            type="button"
            className="nav-drawer__close icon-btn"
            aria-label={t('nav.closeMenu')}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {canUseLobby && (
          <div className="nav-drawer__section" aria-label={t('nav.lobbySection')}>
            {inLobby ? (
              <>
                <span className="lobby-nav__code" aria-label={t('nav.lobbyCode')}>
                  {lobbyCode}
                </span>
                {showJoinLobbyButton && onJoinLobby && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => { onClose(); onJoinLobby(); }}
                  >
                    {t('nav.joinLobbyCta')}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => { void onLeave(); }}
                  disabled={lobbyBusy}
                >
                  {t('nav.leaveLobby')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => { void onCreate(); }}
                  disabled={lobbyBusy}
                >
                  {t('nav.createLobby')}
                </button>
                {showJoinForm ? (
                  <form className="nav-drawer__join" onSubmit={handleJoin}>
                    <input
                      type="text"
                      aria-label={t('nav.lobbyCode')}
                      placeholder={t('nav.lobbyCodePlaceholder')}
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      maxLength={32}
                      autoFocus
                    />
                    <div className="nav-drawer__join-row">
                      <button
                        type="submit"
                        className="btn-ghost"
                        disabled={lobbyBusy || !joinCode.trim()}
                      >
                        {t('nav.join')}
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => { setShowJoinForm(false); setJoinCode(''); }}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setShowJoinForm(true)}
                    disabled={lobbyBusy}
                  >
                    {t('nav.joinLobby')}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {(access || admin) && (
          <div className="nav-drawer__section" aria-label={t('nav.sportsSection')}>
            <Link to="/sports" className="btn-ghost" onClick={onClose}>
              {t('nav.sports')}
            </Link>
            <Link to="/sports-vod" className="btn-ghost" onClick={onClose}>
              {t('nav.sportsVod')}
            </Link>
            {liveTvEnabled && (
              <Link to="/live-tv" className="btn-ghost" onClick={onClose}>
                {t('nav.liveTv')}
              </Link>
            )}
          </div>
        )}

        <div className="nav-drawer__section">
          {isAuthenticated ? (
            <>
              <Link to="/account" className="btn-ghost" onClick={onClose}>
                {t('nav.account')}
              </Link>
              {isRecruiter && recruiterEnabled && (
                <Link to="/recruitment/earnings" className="btn-ghost" onClick={onClose}>
                  {t('recruitment.nav.label')}
                </Link>
              )}
              {admin && (
                <Link to="/admin/access-codes" className="btn-ghost" onClick={onClose}>
                  {t('nav.admin')}
                </Link>
              )}
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { onClose(); onSignOut(); }}
              >
                {t('nav.signOut')}
              </button>
            </>
          ) : (
            <>
              <Link to="/register" className="btn-ghost" onClick={onClose}>
                {t('nav.createAccount')}
              </Link>
              <Link to="/login" className="btn" onClick={onClose}>
                {t('nav.signIn')}
              </Link>
            </>
          )}
        </div>
        </>)}
      </aside>
    </>,
    document.body,
  );
}
